import React, { useEffect, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import type {
  CrmProject,
  DevProject,
  ProjectSource,
  ProjectStatus,
  TaskPriority,
  ProjectLeaderCandidate,
} from '@/types/projects';
import { PROJECT_STATUSES } from '@/types/projects';
import { projectApi } from '@/services/projects/projectApi';
import { useAsyncData } from '@/hooks';
import { XMarkIcon } from '@heroicons/react/24/outline';

import DateInput from '@/components/common/DateInput';

const CRM_PROJECT_TYPES = ['implementation', 'migration', 'upgrade', 'integration', 'consulting', 'other'] as const;

function candidateLabel(user: ProjectLeaderCandidate, globalAdminLabel: string): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email;
  return user.is_global_admin ? `${name} (${globalAdminLabel})` : name;
}

interface CrmContactOption {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

function contactLabel(c: CrmContactOption): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.email;
  return c.email && name !== c.email ? `${name} (${c.email})` : name;
}

interface Props {
  source: ProjectSource;
  project: CrmProject | DevProject;
  onClose: () => void;
  onSaved: () => void;
}

export const ProjectEditModal: React.FC<Props> = ({ source, project, onClose, onSaved }) => {
  const { t } = useTranslation();
  const crm = source === 'crm' ? (project as CrmProject) : null;
  const dev = source === 'dev' ? (project as DevProject) : null;

  const [saving, setSaving] = useState(false);
  const [leaderCandidates, setLeaderCandidates] = useState<ProjectLeaderCandidate[]>([]);
  const [contactCandidates, setContactCandidates] = useState<CrmContactOption[]>([]);
  const [form, setForm] = useState<{
    name: string;
    description: string;
    status: ProjectStatus;
    planned_start: string;
    planned_end: string;
    project_type: string;
    budget: string;
    actual_cost: string;
    billing_type: string;
    hourly_rate: string;
    project_manager_id: string;
    primary_contact_id: string;
    customer_id: string;
    priority: TaskPriority;
    release_target: string;
    owner_id: string;
  }>({
    name: project.name,
    description: project.description || '',
    status: project.status as ProjectStatus,
    planned_start: project.planned_start?.slice(0, 10) || '',
    planned_end: project.planned_end?.slice(0, 10) || '',
    project_type: crm?.project_type || 'implementation',
    budget: crm?.budget?.toString() || '',
    actual_cost: crm?.actual_cost?.toString() || '',
    billing_type: crm?.billing_type || 'fixed',
    hourly_rate: crm?.hourly_rate?.toString() || '',
    project_manager_id: crm?.project_manager_id || '',
    primary_contact_id: crm?.primary_contact_id || '',
    customer_id: crm?.customer_id || '',
    priority: dev?.priority || 'normal',
    release_target: dev?.release_target || '',
    owner_id: dev?.owner_id || '',
  });

  const { data: customers } = useAsyncData<{ id: string; name: string }[]>(
    '/api/crm/customers',
    { initialData: [], skip: source !== 'crm' }
  );

  useEffect(() => {
    let cancelled = false;
    const loadLeaders = async () => {
      try {
        const list = source === 'crm'
          ? await projectApi.listManagerCandidates('crm', form.customer_id || undefined)
          : await projectApi.listManagerCandidates('dev');
        if (cancelled) return;
        setLeaderCandidates(list);
        setForm((f) => {
          const leaderField = source === 'crm' ? 'project_manager_id' : 'owner_id';
          const currentId = f[leaderField];
          if (currentId && !list.some((u) => u.id === currentId)) {
            return { ...f, [leaderField]: '' };
          }
          return f;
        });
      } catch {
        if (!cancelled) setLeaderCandidates([]);
      }
    };
    if (source === 'dev' || form.customer_id) {
      loadLeaders();
    } else {
      setLeaderCandidates([]);
    }
    return () => { cancelled = true; };
  }, [source, form.customer_id]);

  useEffect(() => {
    let cancelled = false;
    const loadContacts = async () => {
      if (source !== 'crm' || !form.customer_id) {
        setContactCandidates([]);
        return;
      }
      try {
        const list = await projectApi.listPrimaryContactCandidates(form.customer_id);
        if (cancelled) return;
        setContactCandidates(list);
        setForm((f) => {
          if (f.primary_contact_id && !list.some((u) => u.id === f.primary_contact_id)) {
            return { ...f, primary_contact_id: '' };
          }
          return f;
        });
      } catch {
        if (!cancelled) setContactCandidates([]);
      }
    };
    loadContacts();
    return () => { cancelled = true; };
  }, [source, form.customer_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        description: form.description || undefined,
        status: form.status,
        planned_start: form.planned_start || undefined,
        planned_end: form.planned_end || undefined,
      };

      if (source === 'crm') {
        payload.project_type = form.project_type;
        payload.budget = form.budget ? parseFloat(form.budget) : undefined;
        payload.actual_cost = form.actual_cost ? parseFloat(form.actual_cost) : undefined;
        payload.billing_type = form.billing_type;
        payload.hourly_rate = form.hourly_rate ? parseFloat(form.hourly_rate) : undefined;
        payload.project_manager_id = form.project_manager_id || undefined;
        payload.primary_contact_id = form.primary_contact_id || null;
        payload.customer_id = form.customer_id || undefined;
      } else {
        payload.priority = form.priority as TaskPriority;
        payload.release_target = form.release_target || undefined;
        payload.owner_id = form.owner_id || undefined;
      }

      await projectApi.update(source, project.id, payload);
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault';
  const labelClass = 'block text-sm text-textSecondary mb-1';
  const globalAdminLabel = t('projects.globalAdmin');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-bgContainer rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-textDefault">{t('projects.editProject')}</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-bgElevated rounded">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className={labelClass}>{t('projects.projectName')}</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('common.description')}</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('common.status')}</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as ProjectStatus })}
              className={inputClass}
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`projects.status.${s}`)}</option>
              ))}
            </select>
          </div>

          {source === 'crm' && (
            <>
              <div>
                <label className={labelClass}>{t('projects.selectCustomer')}</label>
                <select
                  required
                  value={form.customer_id}
                  onChange={(e) => setForm({
                    ...form,
                    customer_id: e.target.value,
                    project_manager_id: '',
                    primary_contact_id: '',
                  })}
                  className={inputClass}
                >
                  <option value="">—</option>
                  {(customers || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>{t('projects.projectType')}</label>
                <select
                  value={form.project_type}
                  onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                  className={inputClass}
                >
                  {CRM_PROJECT_TYPES.map((pt) => (
                    <option key={pt} value={pt}>{t(`projects.types.${pt}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>{t('projects.manager')}</label>
                <p className="text-xs text-textSecondary mb-1">{t('projects.managerCandidatesHint')}</p>
                <select
                  value={form.project_manager_id}
                  onChange={(e) => setForm({ ...form, project_manager_id: e.target.value })}
                  disabled={!form.customer_id}
                  className={inputClass}
                >
                  <option value="">{t('projects.notAssigned')}</option>
                  {leaderCandidates.map((u) => (
                    <option key={u.id} value={u.id}>{candidateLabel(u, globalAdminLabel)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>{t('projects.primaryContact')}</label>
                <p className="text-xs text-textSecondary mb-1">{t('projects.primaryContactHint')}</p>
                <select
                  value={form.primary_contact_id}
                  onChange={(e) => setForm({ ...form, primary_contact_id: e.target.value })}
                  disabled={!form.customer_id}
                  className={inputClass}
                >
                  <option value="">{t('projects.notAssigned')}</option>
                  {contactCandidates.map((c) => (
                    <option key={c.id} value={c.id}>{contactLabel(c)}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>{t('projects.billingType')}</label>
                  <select
                    value={form.billing_type}
                    onChange={(e) => setForm({ ...form, billing_type: e.target.value })}
                    className={inputClass}
                  >
                    {['fixed', 'hourly', 'milestone'].map((bt) => (
                      <option key={bt} value={bt}>{t(`projects.billingTypes.${bt}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('projects.hourlyRate')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                    disabled={form.billing_type !== 'hourly'}
                    placeholder="kr/h"
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>{t('projects.budget')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('projects.actualCost')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.actual_cost}
                    onChange={(e) => setForm({ ...form, actual_cost: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
            </>
          )}

          {source === 'dev' && (
            <>
              <div>
                <label className={labelClass}>{t('projects.owner')}</label>
                <p className="text-xs text-textSecondary mb-1">{t('projects.ownerCandidatesHint')}</p>
                <select
                  value={form.owner_id}
                  onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                  className={inputClass}
                >
                  <option value="">{t('projects.notAssigned')}</option>
                  {leaderCandidates.map((u) => (
                    <option key={u.id} value={u.id}>{candidateLabel(u, globalAdminLabel)}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>{t('projects.priority')}</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}
                    className={inputClass}
                  >
                    {['low', 'normal', 'high', 'urgent'].map((p) => (
                      <option key={p} value={p}>{t(`projects.priorityLevel.${p}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('projects.releaseTarget')}</label>
                  <input
                    value={form.release_target}
                    onChange={(e) => setForm({ ...form, release_target: e.target.value })}
                    placeholder="v1.2.0"
                    className={inputClass}
                  />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>{t('projects.plannedStart')}</label>
              <DateInput value={form.planned_start}
                onChange={(e) => setForm({ ...form, planned_start: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{t('projects.plannedEnd')}</label>
              <DateInput value={form.planned_end}
                onChange={(e) => setForm({ ...form, planned_end: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded-lg">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
